<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): see CriterionEnumInput.vue --
         inheritAttrs: false stops Vue 3 from auto-applying class/style to root, so
         $attrs.class is merged into the existing object-class binding below instead of
         a separate :class attribute (only one :class binding is allowed per element).
         Independent of the recursive <component> below, which already forwards the
         *whole* $attrs (including class) down to each rendered item via its own
         v-bind="$attrs" -- that was already happening before this fix and is unrelated
         to this element's own root class. -->
    <div
        class="CriterionArraySortableInput"
        :class="[
            $attrs.class,
            {
                'CriterionArraySortableInput--focus': focused,
                'CriterionArraySortableInput--objectItems': hasObjectChildren,
                'CriterionArraySortableInput--empty': empty,
            },
        ]"
        :style="$attrs.style"
    >
        <FormElement :label="arrayLabel">
            <template #requiredIndicator>
                {{ translate('requiredLabel') }}
            </template>
            <template #labelHint>
                <div v-if="form.schema.warning" class="JsonSchemaForm__warning">
                    {{ form.schema.warning }}
                </div>
                <div v-else-if="form.schema.hint" class="JsonSchemaForm__hint">
                    {{ form.schema.hint }}
                </div>
                <div v-else-if="focused" class="JsonSchemaForm__typeLabel" :class="TypeLabelClass">
                    <FontAwesomeIcon :icon="faExclamationCircle" />
                    <span>{{ typeLabel }}</span>
                </div>
                <div
                    v-else
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <div v-for="(item, index) in value" :key="index" class="CriterionArraySortableInput__itemContainer">
                <div class="CriterionArraySortableInput__itemContentContainer">
                    <component
                        :is="formElementComponent"
                        :id="`${id}_${index}`"
                        :key="index"
                        class="CriterionArraySortableInput__itemContent"
                        :depth="depth + 1"
                        :form="childrenForm"
                        :value="item"
                        :touched="touched"
                        :required="required"
                        :disabled="disabled"
                        :strings="strings"
                        :deletable="minItems < itemsLength"
                        :description="form.schema.description"
                        :hide-title="hideTitle"
                        :show-error="showError"
                        :form-element-component="formElementComponent"
                        :custom-component-input="customComponentInput"
                        :rbac-disabled="rbacDisabled"
                        :show-validation-errors="showValidationErrors"
                        v-bind="$attrs"
                        @change="onChange(index, $event)"
                        @focus="onChildFocused(index)"
                        @touch="onChildTouched(index)"
                        @delete="onDelete(index)"
                        @input="onInput(index, $event)"
                        @blur="$emit('blur', $event)"
                    >
                        <template v-if="$slots.icon" #icon>
                            <slot name="icon" />
                        </template>
                        <template v-if="$slots.deleteIcon" #deleteIcon>
                            <slot name="deleteIcon" />
                        </template>
                    </component>
                    <div v-if="itemsLength > 1" class="CriterionArraySortableInput__sortButtons">
                        <NewButton
                            variant="flat"
                            color="secondary"
                            :disabled="disabled || rbacDisabled || index === 0"
                            @click="onMoveUp(index)"
                        >
                            <EidIcon
                                :icon-src="faAngleUp"
                                :svg-props="{ width: 20, height: 20 }"
                            />
                        </NewButton>
                        <NewButton
                            variant="flat"
                            color="secondary"
                            :disabled="disabled || rbacDisabled || index === itemsLength - 1"
                            @click="onMoveDown(index)"
                        >
                            <EidIcon
                                :icon-src="faAngleDown"
                                :svg-props="{ width: 20, height: 20 }"
                            />
                        </NewButton>
                    </div>
                </div>
            </div>

            <FormInput force-action-slot>
                <NewButton
                    v-if="!disabled && !rbacDisabled && itemsLength < maxItems"
                    class="CriterionArraySortableInput__addButton font-medium"
                    color="primary"
                    :disabled="!valid"
                    @click="onAdd"
                >
                    <EidIcon :icon-src="faPlus" />
                    {{ addItemLabel }}
                </NewButton>
            </FormInput>
        </FormElement>
    </div>
</template>
<style lang="scss">
    .CriterionArraySortableInput {
        &__sortButtons {
            display: flex;
            gap: 12px;
            margin-bottom: 12px;
        }
    }
</style>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import cloneDeep from 'lodash/cloneDeep';
    import startCase from 'lodash/startCase';

    import { faPlus, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
    import { faAngleDown, faAngleUp, faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import { JsonFormArray } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaString } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isObject } from '@evidentid/json-schema/schemaChecks';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AbstractCriterionInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/AbstractCriterionInput';

    function swapArrayItems(list: any[], index1: number, index2: number): any[] {
        const newList = cloneDeep(list);
        [ newList[index1], newList[index2] ] = [ newList[index2], newList[index1] ];
        return newList;
    }

    @Component({
        emits: [ 'blur', 'change', 'input' ],
        components: { FormInput, FormElement, NewButton, FontAwesomeIcon, EidIcon },
        inheritAttrs: false,
    })
    export default class CriterionArraySortableInput extends AbstractCriterionInput<JsonFormArray, any[]> {
        private faPlus = faPlus;
        private faExclamationCircle = faExclamationCircle;
        private faQuestionCircle = faQuestionCircle;
        private faAngleUp = faAngleUp;
        private faAngleDown = faAngleDown;
        private childAccessedList: boolean[] = new Array(this.itemsLength);
        private focusedChildIndex: number | null = null;

        private mounted(): void {
            if (!this.value || !Array.isArray(this.value) || this.value.length >= this.minItems) {
                return;
            }
            const missingItems = this.minItems - this.value.length;
            const newList = [ ...this.value ].concat(
                Array.from(new Array(missingItems)).map(() => this.form.item.getValue()),
            );
            this.childAccessedList = this.childAccessedList.concat(
                Array.from(new Array(missingItems)).map(() => false),
            );
            this.emitInputAndChange(newList);
        }

        private get itemsLength(): number {
            return Array.isArray(this.value) ? this.value.length : 0;
        }

        private get minItems(): number {
            return this.form.schema.minItems || 0;
        }

        private get maxItems(): number {
            return this.form.schema.maxItems == null ? Infinity : this.form.schema.maxItems;
        }

        private get typeLabel(): string {
            const stringFormat = (this.form.item.schema as JsonSchemaString).format;
            const label = this.form.item.type === 'string' && stringFormat ? stringFormat : this.form.item.type;
            return `${label.charAt(0).toUpperCase()}${label.substring(1).toLowerCase()} only`;
        }

        protected get TypeLabelClass(): string | null {
            if (!this.focused || this.focusedChildIndex == null) {
                return null;
            }
            return this.childAccessedList[this.focusedChildIndex] && !this.isChildValid(this.focusedChildIndex)
                ? 'JsonSchemaForm__typeLabel--error'
                : 'JsonSchemaForm__typeLabel--focused';
        }

        private get childrenForm(): any {
            // give parent's title if items does not have one.
            return {
                ...this.form.item,
                schema: {
                    ...this.form.item.schema,
                    title: this.form.schema.title,
                    description: this.form.schema.description,
                },
            };
        }

        private get addItemLabel(): string {
            return this.form.schema.addItemTitle || `Add ${startCase(this.form.schema.title)}`;
        }

        private get arrayLabel(): string {
            return this.itemsLength && this.hasObjectChildren && this.form.schema.title
                ? this.form.schema.title
                : '';
        }

        private get hasObjectChildren(): boolean {
            return isObject(this.form.item.schema);
        }

        private isChildValid(index: number): boolean {
            return !this.required && !this.value[index] ? true : this.form.item.isValid(this.value[index]);
        }

        private onChildFocused(index: number): void {
            this.focused = true;
            this.focusedChildIndex = index;
        }

        private onChildTouched(index: number): void {
            this.focused = false;
            this.childAccessedList[index] = true;
            this.focusedChildIndex = null;
        }

        private onInput(index: number, value: any): void {
            this.$emit('input', this.form.getValue([
                ...this.value.slice(0, index),
                value,
                ...this.value.slice(index + 1),
            ]));
        }

        private onChange(index: number, value: any): void {
            this.$emit('change', this.form.getValue([
                ...this.value.slice(0, index),
                value,
                ...this.value.slice(index + 1),
            ]));
        }

        private emitInputAndChange(value: any): void {
            this.$emit('input', value);
            this.$emit('change', value);
        }

        private onAdd(): void {
            if (this.valid) {
                this.childAccessedList.push(false);
                const newVal = this.form.getValue([
                    ...(this.value || []),
                    this.form.item.getValue(),
                ]);
                this.emitInputAndChange(newVal);
            }
        }

        protected onDelete(index: number): void {
            this.childAccessedList = [
                ...this.childAccessedList.slice(0, index),
                ...this.childAccessedList.slice(index + 1),
            ];
            const newVal = this.form.getValue([
                ...this.value.slice(0, index),
                ...this.value.slice(index + 1),
            ]);
            this.emitInputAndChange(newVal);
        }

        private onMoveUp(index: number): void {
            if (index === 0) {
                return;
            }
            this.childAccessedList = swapArrayItems(this.childAccessedList, index - 1, index);
            const newVal = this.form.getValue(swapArrayItems(this.value, index - 1, index));
            this.emitInputAndChange(newVal);
        }

        private onMoveDown(index: number): void {
            if (index === this.itemsLength - 1) {
                return;
            }
            this.childAccessedList = swapArrayItems(this.childAccessedList, index, index + 1);
            const newVal = this.form.getValue(swapArrayItems(this.value, index, index + 1));
            this.emitInputAndChange(newVal);
        }
    }
</script>
